/** The parts of the OpenAPI document of `GET /api-docs/openapi.json` the reference shows. */
export interface JsonSchema {
  type?: string | string[];
  format?: string;
  enum?: unknown[];
  const?: unknown;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  maxItems?: number;
  pattern?: string;
  default?: unknown;
}

export interface ApiOperation {
  operationId: string;
  summary?: string;
  description?: string;
  tags: string[];
  security: object[];
  'x-permission'?: string;
  'x-scope'?: string;
  'x-public'?: boolean;
  parameters: { name: string; in: string; required: boolean; schema: JsonSchema }[];
  requestBody?: { content: { 'application/json': { schema: JsonSchema } } };
  responses: Record<string, { description: string }>;
}

export interface OpenApiDocument {
  paths: Record<string, Record<string, ApiOperation>>;
}

export interface Endpoint {
  method: string;
  path: string;
  operation: ApiOperation;
}

export function endpoints(doc: OpenApiDocument): Endpoint[] {
  return Object.entries(doc.paths).flatMap(([path, methods]) =>
    Object.entries(methods).map(([method, operation]) => ({ method, path, operation })),
  );
}

/** Short type of a schema: `string (uuid)`, `string[]`, `"a", "b"`, `number, null`. */
export function typeOf(schema: JsonSchema): string {
  if (schema.enum) return schema.enum.map((v) => JSON.stringify(v)).join(', ');
  if (schema.const !== undefined) return JSON.stringify(schema.const);
  const variants = schema.anyOf ?? schema.oneOf;
  if (variants) return [...new Set(variants.map(typeOf))].join(', ');
  if (schema.type === 'array') return `${schema.items ? typeOf(schema.items) : 'any'}[]`;
  const type = Array.isArray(schema.type) ? schema.type.join(', ') : (schema.type ?? 'any');
  return schema.format ? `${type} (${schema.format})` : type;
}

/** Limits of a value in words of the reference: lengths, ranges, pattern, default. */
export function rulesOf(
  schema: JsonSchema,
  labels: Record<'length' | 'range' | 'items' | 'default', string>,
): string[] {
  const rules: string[] = [];
  const range = (min?: number, max?: number) =>
    min !== undefined && max !== undefined
      ? `${min}–${max}`
      : min !== undefined
        ? `≥ ${min}`
        : max !== undefined
          ? `≤ ${max}`
          : undefined;
  const length = range(schema.minLength, schema.maxLength);
  if (length) rules.push(`${labels.length} ${length}`);
  const values = range(schema.minimum, schema.maximum);
  if (values) rules.push(`${labels.range} ${values}`);
  const items = range(schema.minItems, schema.maxItems);
  if (items) rules.push(`${labels.items} ${items}`);
  if (schema.pattern) rules.push(`\`${schema.pattern}\``);
  if (schema.default !== undefined)
    rules.push(`${labels.default} ${JSON.stringify(schema.default)}`);
  return rules;
}

export interface Field {
  name: string;
  schema: JsonSchema;
  required: boolean;
}

/** Fields of an object schema, nested objects and object arrays as `parent.child`. */
export function fieldsOf(schema: JsonSchema, prefix = ''): Field[] {
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties ?? {}).flatMap(([name, property]) => {
    const path = `${prefix}${name}`;
    const own = { name: path, schema: property, required: required.has(name) };
    const object = property.items?.properties ? property.items : property;
    const nested = object.properties
      ? fieldsOf(object, `${path}${object === property ? '' : '[]'}.`)
      : [];
    return [own, ...nested];
  });
}

/** Text for a Markdown table cell. */
export function cell(text: string): string {
  return text.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}
